import { useIntl } from '@common/i18n';
import {
  Box,
  Button,
  Flex,
  Grid,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
} from '@common/ui-components';
import { dayjs } from '@libs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { MdOutlineCalendarToday } from 'react-icons/md';

import { dateRangePickerIntlIds } from './intl';

export type DateRangePickerProps = {
  startAt?: string;
  endAt?: string;
  onRangePick: (key: string, value?: string) => void;
};

export function DateRangePicker({
  startAt,
  endAt,
  onRangePick,
}: DateRangePickerProps) {
  const ref = useRef<HTMLDivElement>(null);

  const { formatMessage } = useIntl();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const today = dayjs().startOf('day');

  const [currentMonth, setCurrentMonth] = useState(today);
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null);
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(null);
  const [dateOnRange, setDateOnRange] = useState<dayjs.Dayjs | null>(null);

  useEffect(() => {
    if (startAt && dayjs(startAt).isValid()) {
      setStartDate(dayjs(startAt));
    } else {
      setStartDate(null);
    }

    if (endAt && dayjs(endAt).isValid()) {
      setEndDate(dayjs(endAt));
    } else {
      setEndDate(null);
    }
  }, [startAt, endAt]);

  function getInputDisplayValue() {
    if (!startDate) return;
    if (!endDate) return startDate.format('DD/MM/YYYY');

    return `${startDate.format('DD/MM/YYYY')} - ${endDate.format(
      'DD/MM/YYYY'
    )}`;
  }

  const getMonthDays = useMemo(() => {
    const startOfMonth = currentMonth.startOf('month');
    const endOfMonth = currentMonth.endOf('month');

    const days = [];

    for (
      let day = startOfMonth;
      day.isBefore(endOfMonth) || day.isSame(endOfMonth, 'day');
      day = day.add(1, 'day')
    ) {
      days.push(day);
    }

    return days;
  }, [currentMonth]);

  const isInRange = useCallback(
    (date: dayjs.Dayjs) => {
      if (!startDate) return false;

      if (!endDate && dateOnRange && dateOnRange.isAfter(startDate)) {
        return date.isAfter(startDate) && date.isBefore(dateOnRange);
      }

      if (endDate) {
        return date.isBetween(startDate, endDate, null, '()');
      }

      return false;
    },
    [startDate, endDate, dateOnRange]
  );

  function handleDateClick(date: dayjs.Dayjs) {
    if (date.isBefore(today)) return;

    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
    } else {
      if (date.isBefore(startDate)) {
        setEndDate(startDate);
        setStartDate(date);
      } else {
        setEndDate(date);
      }
    }
  }

  function handleClearPeriod() {
    setStartDate(null);
    setEndDate(null);

    onRangePick('startAt', undefined);
    onRangePick('endAt', undefined);
  }

  const getButtonDayStyle = useCallback(
    (date: dayjs.Dayjs) => {
      const day = date.date();

      const isToday = date.isSame(today, 'day');
      const isPastDay = date.isBefore(today);
      const isSelected =
        (startDate && date.isSame(startDate, 'day')) ||
        (endDate && date.isSame(endDate, 'day'));
      const isRangeDay = isInRange(date);
      const isHovered = dateOnRange && date.isSame(dateOnRange, 'day');

      const style = {
        variant: 'ghost' as const,
        bg: undefined,
        color: 'blue.800',
        children: day,
      };

      if (isSelected) {
        return {
          ...style,
          variant: 'solid' as const,
          bg: 'blue.500',
          color: 'white',
        };
      } else if (isRangeDay) {
        return {
          ...style,
          bg: 'blue.100',
        };
      } else if (isHovered) {
        return {
          ...style,
          _hover: {
            bg: 'blue.100',
          },
        };
      } else if (isPastDay) {
        return {
          ...style,
          textDecoration: 'line-through',
          color: 'gray.400',
          isDisabled: true,
        };
      } else if (isToday) {
        return {
          ...style,
          border: '1px solid',
          borderColor: 'blue.500',
          color: 'blue.500',
        };
      }

      return style;
    },
    [today, dateOnRange, startDate, endDate, isInRange]
  );

  const buttonStyles = useMemo(() => {
    const styles = new Map<string, unknown>();

    getMonthDays.forEach((date) => {
      const key = date.format('YYYY-MM-DD');
      styles.set(key, getButtonDayStyle(date));
    });

    return styles;
  }, [getMonthDays, getButtonDayStyle]);

  function handleRangePick() {
    if (!startDate || !endDate) return;

    onRangePick('startAt', startDate.format('YYYY-MM-DD'));
    onRangePick('endAt', endDate.format('YYYY-MM-DD'));

    onClose();
  }

  return (
    <Box minWidth="24rem">
      <Popover
        placement="bottom-start"
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        autoFocus={false}
        returnFocusOnClose={false}
        matchWidth
        isLazy
      >
        <PopoverTrigger>
          <InputGroup>
            <Input
              value={getInputDisplayValue() ?? ''}
              placeholder={formatMessage({
                id: dateRangePickerIntlIds.selectPeriodOrDateInput.placeholder,
              })}
              cursor="pointer"
              isReadOnly
            />
            <InputRightElement
              pointerEvents="none"
              color={isOpen ? 'blue.500' : 'gray.800'}
            >
              <MdOutlineCalendarToday color="inherit" />
            </InputRightElement>
          </InputGroup>
        </PopoverTrigger>

        <PopoverContent ref={ref} width="100%" padding={4}>
          <PopoverArrow />

          <Flex flexDirection="column" alignItems="stretch" gap={6}>
            <Flex alignItems="center" justifyContent="space-between">
              <IconButton
                aria-label={formatMessage({
                  id: dateRangePickerIntlIds.previousMonthAria.label,
                })}
                icon={<IoIosArrowBack />}
                size="sm"
                onClick={() =>
                  setCurrentMonth(currentMonth.subtract(1, 'month'))
                }
                variant="ghost"
                colorScheme="blue"
              />
              <Text>
                {currentMonth
                  .locale(window.navigator.language)
                  .format('MMMM, YYYY')}
              </Text>
              <IconButton
                aria-label={formatMessage({
                  id: dateRangePickerIntlIds.nextMonthAria.label,
                })}
                icon={<IoIosArrowForward />}
                size="sm"
                onClick={() => setCurrentMonth(currentMonth.add(1, 'month'))}
                variant="ghost"
                colorScheme="blue"
              />
            </Flex>

            <Grid
              templateColumns="repeat(7, 1fr)"
              alignItems="center"
              justifyItems="center"
              gap={1}
            >
              {dayjs()
                .locale(window.navigator.language)
                .localeData()
                .weekdaysShort()
                .map((weekday) => (
                  <Text key={weekday} fontSize="sm" color="gray.600">
                    {weekday}
                  </Text>
                ))}

              {getMonthDays.map((date) => {
                const key = date.format('YYYY-MM-DD');
                const style = buttonStyles.get(key) ?? {};

                return (
                  <Button
                    key={key}
                    width={10}
                    height={10}
                    rounded="full"
                    onClick={() => handleDateClick(date)}
                    onMouseEnter={() => setDateOnRange(date)}
                    {...style}
                  />
                );
              })}
            </Grid>

            <Flex alignItems="center" justifyContent="space-between">
              <Button
                variant="link"
                colorScheme="blue"
                onClick={() => setCurrentMonth(today)}
              >
                {formatMessage({
                  id: dateRangePickerIntlIds.goBackToTodayButton.label,
                })}
              </Button>

              <Flex gap={2}>
                <Button
                  variant="outline"
                  onClick={startDate ? handleClearPeriod : onClose}
                >
                  {startDate
                    ? formatMessage({
                        id: dateRangePickerIntlIds.clearSelectedPeriodButton
                          .label,
                      })
                    : formatMessage({
                        id: dateRangePickerIntlIds.cancelButton.label,
                      })}
                </Button>

                <Button
                  colorScheme="blue"
                  isDisabled={!startDate || !endDate}
                  onClick={handleRangePick}
                >
                  {formatMessage({
                    id: dateRangePickerIntlIds.selectPeriodButton.label,
                  })}
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </PopoverContent>
      </Popover>
    </Box>
  );
}
